import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Copia di cortesia (stampabile) di una fattura elettronica.
 *
 * ATTENZIONE: è un documento SENZA valore fiscale. L'originale a norma è il file
 * XML FatturaPA trasmesso al Sistema di Interscambio. Questo PDF serve solo per
 * lettura/archiviazione interna o da inviare al cliente come promemoria.
 *
 * Riceve il record completo da GET /api/fe/{id} (con righe + snapshot
 * cedente/cessionario). Generazione client-side con jsPDF (come exportAnalyticsPDF).
 */

const C = {
  bg: [7, 7, 26], cyan: [0, 180, 220], text: [30, 35, 50],
  muted: [110, 120, 140], border: [220, 225, 235], bgLight: [248, 249, 252],
  white: [255, 255, 255],
};

const eur = (n) => '€ ' + new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const num = (n, d = 2) => new Intl.NumberFormat('it-IT', { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(n) || 0);

const TIPI = {
  TD01: 'Fattura', TD02: 'Acconto/Anticipo su fattura', TD04: 'Nota di credito',
  TD05: 'Nota di debito', TD06: 'Parcella', TD24: 'Fattura differita',
};

function parseJson(v) {
  if (!v) return {};
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return {}; } }
  return v;
}

function anagraficaLines(a, isCliente) {
  const nome = a.denominazione || a.azienda || [a.nome, a.cognome].filter(Boolean).join(' ') || '—';
  const via = isCliente ? a.indirizzo_via : a.sede_via;
  const cap = isCliente ? a.indirizzo_cap : a.sede_cap;
  const comune = isCliente ? a.indirizzo_comune : a.sede_comune;
  const prov = isCliente ? a.indirizzo_provincia : a.sede_provincia;
  const naz = (isCliente ? a.indirizzo_nazione : a.sede_nazione) || 'IT';
  const lines = [nome];
  if (via) lines.push(via);
  const cityLine = [cap, comune, prov ? `(${prov})` : ''].filter(Boolean).join(' ');
  if (cityLine) lines.push(cityLine + (naz && naz !== 'IT' ? ` - ${naz}` : ''));
  if (a.piva) lines.push(`P.IVA ${a.piva}`);
  if (a.codice_fiscale && a.codice_fiscale !== a.piva) lines.push(`C.F. ${a.codice_fiscale}`);
  if (isCliente) {
    if (a.codice_destinatario) lines.push(`Cod. Dest. ${a.codice_destinatario}`);
    else if (a.pec) lines.push(`PEC ${a.pec}`);
  }
  return lines;
}

function box(doc, x, y, w, h, titolo, righe) {
  doc.setDrawColor(...C.border); doc.setLineWidth(0.3);
  doc.setFillColor(...C.bgLight);
  doc.roundedRect(x, y, w, h, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...C.cyan);
  doc.text(titolo.toUpperCase(), x + 4, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.text);
  let ly = y + 12;
  righe.forEach((r, i) => {
    if (i === 0) { doc.setFont('helvetica', 'bold'); doc.setFontSize(10); }
    else { doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...C.muted); }
    doc.text(String(r).slice(0, 60), x + 4, ly);
    ly += (i === 0 ? 6 : 4.6);
  });
}

export function generateFatturaPDF(f) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, M = 16;
  const ced = parseJson(f.cedente);
  const cess = parseJson(f.cessionario);
  const righe = Array.isArray(f.righe) ? f.righe : parseJson(f.righe) || [];
  const dataStr = f.data_documento ? new Date(f.data_documento).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const numeroDoc = `${f.numero}/${f.anno}`;

  // ── Header ──
  doc.setFillColor(...C.bg); doc.rect(0, 0, W, 30, 'F');
  doc.setFillColor(...C.cyan); doc.rect(0, 0, 4, 30, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.cyan);
  doc.text('EDENFLOW', M, 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(180, 190, 215);
  doc.text('Copia di cortesia — fattura elettronica', M, 19);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...C.white);
  doc.text(TIPI[f.tipo_documento] || 'Fattura', W - M, 13, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(200, 210, 230);
  doc.text(`N. ${numeroDoc} del ${dataStr}`, W - M, 20, { align: 'right' });

  // ── Cedente / Cessionario ──
  const boxY = 38, boxW = (W - M * 2 - 8) / 2, boxH = 40;
  box(doc, M, boxY, boxW, boxH, 'Cedente / Prestatore', anagraficaLines(ced, false));
  box(doc, M + boxW + 8, boxY, boxW, boxH, 'Cessionario / Committente', anagraficaLines(cess, true));

  // ── Righe ──
  let y = boxY + boxH + 8;
  const body = righe.map((r, i) => [
    String(i + 1),
    r.descrizione || '',
    num(r.quantita || 1),
    eur(r.prezzo_unitario),
    r.natura ? r.natura : `${num(r.aliquota, 2)}%`,
    eur(r.prezzo_totale != null ? r.prezzo_totale : (Number(r.quantita || 1) * Number(r.prezzo_unitario || 0))),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['#', 'Descrizione', 'Q.tà', 'Prezzo unit.', 'IVA', 'Importo']],
    body,
    theme: 'grid',
    headStyles: { fillColor: C.bg, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    bodyStyles: { fontSize: 8.5, textColor: C.text },
    alternateRowStyles: { fillColor: C.bgLight },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 16, halign: 'right' },
      3: { cellWidth: 26, halign: 'right' },
      4: { cellWidth: 20, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
    },
    margin: { left: M, right: M },
  });

  // ── Totali ──
  y = doc.lastAutoTable.finalY + 8;
  if (y > 240) { doc.addPage(); y = 20; }
  const tW = 78, tX = W - M - tW;
  const rows = [['Imponibile', eur(f.imponibile)]];
  if (Number(f.imposta) > 0) rows.push(['IVA', eur(f.imposta)]);
  if (Number(f.bollo) > 0) rows.push(['Bollo', eur(f.bollo)]);
  rows.forEach((r, i) => {
    const ry = y + i * 7;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.muted);
    doc.text(r[0], tX, ry);
    doc.setTextColor(...C.text);
    doc.text(r[1], W - M, ry, { align: 'right' });
  });
  const totY = y + rows.length * 7 + 2;
  doc.setFillColor(...C.bg); doc.roundedRect(tX - 4, totY - 5, tW + 4, 11, 2, 2, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...C.white);
  doc.text('Totale documento', tX, totY + 2.5);
  doc.setTextColor(...C.cyan);
  doc.text(eur(f.totale), W - M, totY + 2.5, { align: 'right' });

  // ── Causale + note regime/pagamento ──
  let ny = totY + 16;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...C.muted);
  if (f.causale) { doc.text(`Causale: ${f.causale}`, M, ny); ny += 5; }
  const natura = righe.find(r => r.natura)?.natura;
  if (natura) { doc.text('Operazione senza applicazione IVA (vedi Natura ' + natura + ').', M, ny); ny += 5; }
  if (ced.iban) { doc.text(`Pagamento: bonifico — IBAN ${ced.iban}`, M, ny); ny += 5; }
  if (f.xml_filename) { doc.text(`File XML: ${f.xml_filename}`, M, ny); ny += 5; }

  // ── Footer / disclaimer ──
  doc.setDrawColor(...C.border); doc.setLineWidth(0.4); doc.line(M, 282, W - M, 282);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(...C.muted);
  doc.text('Copia di cortesia priva di valore fiscale. L\'originale a norma è la fattura elettronica XML trasmessa al Sistema di Interscambio (SDI).', M, 287, { maxWidth: W - M * 2 });
  doc.text('EdenFlow', W - M, 292, { align: 'right' });

  doc.save(`fattura_${f.numero}_${f.anno}.pdf`);
}
